import { useMemo, ReactElement } from 'react'
import _ from 'lodash'

// components
import { MultiSelect } from 'components/common'

import type { FilterSpec } from 'types/filter'
import type { MultiSelectProp } from 'components/common/MultiSelect'

import scss from '../index.module.scss'

import { SelectDropdown } from '../FilterForm'
import FilterTrigger from '../FilterTrigger'
import { getCommonSelectProps, getFilterDropdownBadgeLabel } from '../utils'
import useFiltersDropdown from '../hooks/useFiltersDropdown'

export type FilterDropdownProps = FilterSpec &
  MultiSelectProp & {
    title?: string
    initialIsDropdownOpen?: boolean
    target?: ReactElement
  }

const FilterDropdown = ({
  value = [],
  options = [],
  onChange,
  title,
  isMulti = true,
  isClearable = true,
  formatOptionLabel,
  useOptionValueOnly,
  target: TargetComponent,
  initialIsDropdownOpen,
  ...rest
}: FilterDropdownProps): ReactElement => {
  const { isOpen, toggleOpen, ref } = useFiltersDropdown(
    value,
    initialIsDropdownOpen
  )

  const selectedOptions = useMemo(() => {
    const validValue = isMulti ? value : [value]
    return _(validValue)
      .map(v => _.find(options, { value: v }))
      .compact()
      .value()
  }, [isMulti, value, options])

  const labels = useMemo(
    () => getFilterDropdownBadgeLabel(selectedOptions),
    [selectedOptions]
  )

  return (
    <div className='d-flex align-items-center flex-wrap'>
      <SelectDropdown
        isOpen={isOpen}
        onClose={toggleOpen}
        ref={ref}
        target={
          TargetComponent ? (
            <TargetComponent onClick={toggleOpen} />
          ) : (
            <FilterTrigger
              title={title}
              labels={labels}
              toggleDropdown={toggleOpen}
            />
          )
        }
      >
        <div className={scss.dropdown}>
          <MultiSelect
            {...rest}
            {...getCommonSelectProps({
              value,
              isMulti,
              useOptionValueOnly,
              onChange,
              toggleOpen,
              formatOptionLabel,
            })}
            menuIsOpen
            options={options}
          />
        </div>
      </SelectDropdown>
    </div>
  )
}

export default FilterDropdown
